<template>
  <!-- Backdrop -->
  <Transition name="fade">
    <div v-if="isOpen" class="drawer-backdrop" @click="closeDrawer"></div>
  </Transition>

  <!-- Drawer -->
  <Transition name="slide">
    <div v-if="isOpen" class="drawer-mobile-web">
      <div class="drawer-header">
        <div class="drawer-title">
          <div class="drawer-logo"></div>
        </div>
        <IonButton fill="clear" class="close-btn" @click="closeDrawer">
          <IonIcon :icon="closeOutline" slot="icon-only" />
        </IonButton>
      </div>

      <component :is="drawerContentComponent" @close="closeDrawer" />
    </div>
  </Transition>
</template>

<script setup lang="ts">
import { computed, type Component } from 'vue';
import { IonButton, IonIcon } from '@ionic/vue';
import { closeOutline } from 'ionicons/icons';
import { useUIStore } from '@/common/stores/uiStore';
import { useLayout } from '@/layouts/composables/useLayout';
import { USER_TYPE } from '@/common/constants';
import { DrawerContentStudent, DrawerContentEmployee } from '@/layouts/components/menu-content';

// Mapping des composants drawer par type utilisateur (USER_TYPE)
const DRAWER_CONTENT_COMPONENTS: Record<string, Component> = {
  [USER_TYPE.STUDENT]: DrawerContentStudent,
  [USER_TYPE.COLLABORATEUR]: DrawerContentEmployee,
  [USER_TYPE.ADMINISTRATOR]: DrawerContentEmployee,
};

defineProps<{
  isOpen: boolean;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
}>();

const uiStore = useUIStore();
const { userType } = useLayout();

const drawerContentComponent = computed<Component>(() => {
  const type = userType.value ?? USER_TYPE.STUDENT;
  return DRAWER_CONTENT_COMPONENTS[type] ?? DrawerContentStudent;
});

function closeDrawer() {
  uiStore.closeDrawer();
  emit('close');
}
</script>

<style scoped>
.drawer-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1100;
}

.drawer-mobile-web {
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: 85%;
  max-width: 320px;
  background: var(--ion-background-color);
  z-index: 1200;
  display: flex;
  flex-direction: column;
  box-shadow: var(--ion-box-shadow-hover);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-bottom: 1px solid var(--ion-border-color);
  flex-shrink: 0;
}

.drawer-title {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  width: 100%;
}

.drawer-logo {
  width: 64px;
  height: 64px;
  background-color: var(--ion-color-primary);
  -webkit-mask: url('@/assets/images/emn-logo-transparent.svg') no-repeat center;
  -webkit-mask-size: contain;
  mask: url('@/assets/images/emn-logo-transparent.svg') no-repeat center;
  mask-size: contain;
}

.drawer-title-text {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--ion-text-color);
}

.close-btn {
  --padding-start: 8px;
  --padding-end: 8px;
}

.close-btn ion-icon {
  font-size: 24px;
  color: var(--ion-text-color);
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(-100%);
}
</style>
